import React, { FC, useState } from 'react';

import { Checkbox } from '@rbilabs/universal-components';
import { CCFormField, CardType, IPaymentErrors, IPaymentState } from 'features/payment/types';
import { View } from 'native-base';
import { StyleSheet } from 'react-native';

import { TextInput } from 'components/ucl/text-input';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import { Box, Typography } from 'components/ui';
import useCreditCardFormProps from 'hooks/form/use-credit-card-form-props';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  defaultPaymentFieldVariation,
} from 'state/launchdarkly/variations';
import { primitive } from 'styles/constants';

import FirstDataCreditCardInputsRedesign from './first-data-credit-card-form-inputs/first-data-credit-card-form-inputs-redesign';
import {
  StyledBillingZipWrapper,
  StyledNameOnCardWrapper,
  StyledSaveCardContainer,
} from './styled';
import { getErrorMessage } from './utils';

interface ICreditCardFormInputs {
  errors: IPaymentErrors;
  isDelivery?: boolean;
  onChange: (name: CCFormField, value: string) => void;
  paymentValues: IPaymentState;
  showSaveCard?: boolean;
  ariaLabel?: string;
  supportedCardTypes?: CardType[];
  validateCreditCardForm?: () => boolean;
  isPrepaidReload?: boolean;
  isUpdate?: boolean;
  isQuickPayMethodSelected?: boolean;
}

const CreditCardFormInputs: FC<React.PropsWithChildren<ICreditCardFormInputs>> = ({
  errors,
  isDelivery = false,
  onChange,
  paymentValues,
  showSaveCard = true,
  ariaLabel,
  supportedCardTypes = [],
  validateCreditCardForm,
  isPrepaidReload = false,
  isUpdate = false,
  isQuickPayMethodSelected = false,
}) => {
  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
    defaultPaymentFieldVariation;
  const { feCountryCode } = useLocale();
  // If the country field is hidden from the form, use the user's current locale
  const country = paymentFieldVariations.country ? paymentValues.billingCountry : feCountryCode;

  // retrieve additional credit card form props
  const { postalCodeInputProps } = useCreditCardFormProps(country ?? undefined);

  const errorMessage = getErrorMessage({ errors });

  const [isCardNameInputFocused, setIsCardNameInputFocused] = useState(false);
  const [isZipCodeInputFocused, setIsZipCodeInputFocused] = useState(false);

  // NativeBase is having a weird behavior with the Checkbox
  // The onChange handler passed to it is always stale, unless we force a re-render by changing its key
  const checkboxKey = React.useMemo(() => JSON.stringify(paymentValues), [paymentValues]);

  const [didBlur, setDidBlur] = useState({
    cardNumber: !!paymentValues.cardNumber,
    expiry: !!paymentValues.expiry,
    cvv: !!paymentValues.cvv,
    billingZip: !!paymentValues.billingZip,
  });

  const validateOnBlur = (fieldName: string) => {
    setDidBlur({ ...didBlur, [fieldName]: true } as any);
    validateCreditCardForm?.();
  };
  return (
    <>
      {!!errorMessage && <VisuallyHidden accessibilityLabel={errorMessage} role="alert" />}
      <View accessibilityLabel={ariaLabel} style={styles.formContainer}>
        <FirstDataCreditCardInputsRedesign
          didBlur={didBlur}
          validateOnBlur={validateOnBlur}
          onChange={onChange}
          supportedCardTypes={supportedCardTypes}
          paymentValues={paymentValues}
          validateCreditCardForm={validateCreditCardForm}
          errors={errors}
          paymentFieldVariations={paymentFieldVariations}
        />
        {paymentFieldVariations.name && (
          <StyledNameOnCardWrapper>
            <TextInput
              borderColor={primitive.$blackOpacity30}
              accessibilityLabel={
                !errors.nameOnCard ? t('nameOnCard') : t('pleaseProvideTheCardName')
              }
              onFocus={() => setIsCardNameInputFocused(true)}
              onBlur={() => {
                setIsCardNameInputFocused(false);
              }}
              onChangeText={(value: string) => onChange('nameOnCard', value)}
              label={t('nameOnCard')}
              value={paymentValues.nameOnCard}
              placeholder={isCardNameInputFocused ? 'John Smith' : ''}
              placeholderTextColor={primitive.$blackOpacity30}
              required
              testID="nameOnCard"
              errorMessage={errors.nameOnCard}
              autoComplete="name"
            />
          </StyledNameOnCardWrapper>
        )}

        {isDelivery && (
          <Checkbox
            key={checkboxKey}
            onChange={value => onChange('billingAddressSameAsDelivery', value ? 'true' : 'false')}
            value={String(paymentValues.billingAddressSameAsDelivery)}
            isChecked={paymentValues.billingAddressSameAsDelivery}
            testID="billing-same-as-delivery"
          >
            {t('billingAddressIsSameAsDeliveryAddress')}
          </Checkbox>
        )}
        {paymentFieldVariations.zip && (
          <StyledBillingZipWrapper>
            {/* Additional props are spread: label, maxLength, pattern */}
            <TextInput
              borderColor={primitive.$blackOpacity30}
              onChangeText={value => onChange('billingZip', value)}
              value={paymentValues.billingZip}
              required
              testID="billingZip"
              accessibilityLabel={
                !didBlur.billingZip && !errors.didAttemptSubmit
                  ? t('zipCode')
                  : t('pleaseProvideTheZipCode')
              }
              onFocus={() => setIsZipCodeInputFocused(true)}
              onBlur={() => {
                validateOnBlur('billingZip');
                setIsZipCodeInputFocused(false);
              }}
              {...(isZipCodeInputFocused
                ? {
                    placeholder: t('zipCode'),
                    placeholderTextColor: primitive.$blackOpacity30,
                  }
                : null)}
              errorMessage={
                !didBlur.billingZip && !errors.didAttemptSubmit ? '' : errors.billingZip
              }
              placeholderTextColor={primitive.$blackOpacity30}
              autoComplete="postal-code"
              maxLength={10}
              isDisabled={isDelivery && paymentValues.billingAddressSameAsDelivery}
              {...postalCodeInputProps}
            />
          </StyledBillingZipWrapper>
        )}
      </View>

      {showSaveCard && (
        <StyledSaveCardContainer>
          {isPrepaidReload || isUpdate ? (
            <Box style={styles.saveCardDisclaimer}>
              <Typography>{t('saveCardDisclaimerRedesign')}</Typography>
            </Box>
          ) : (
            <Checkbox
              testID="saveCard"
              key={checkboxKey}
              value={String(paymentValues.saveCard)}
              isChecked={paymentValues.saveCard}
              onChange={value => {
                onChange('saveCard', value ? 'true' : 'false');
              }}
              accessibilityLabel={t('saveCardDisclaimer')}
              _text={{ fontSize: 'sm' }}
              justifyContent="flex-start"
              marginTop="$2"
            >
              {t('saveCardDisclaimerRedesign')}
            </Checkbox>
          )}
        </StyledSaveCardContainer>
      )}
    </>
  );
};
const styles = StyleSheet.create({
  formContainer: {
    backgroundColor: primitive.$white,
    padding: 16,
    borderRadius: 10,
  },
  saveCardDisclaimer: {
    marginTop: 8,
  },
});

export default CreditCardFormInputs;
